import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Spacing } from '@reservamos/elements';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import { isPayPalV6Enabled } from 'utils/paypalV6Config';
import CheckList from '../../ui/atoms/CheckList';
import PaymentImage from '../../ui/atoms/PaymentImage';

/**
 * PaypalForm component.
 * Displays PayPal payment form with instructions.
 * @returns {JSX.Element} PayPal payment form with instructions.
 */
const PaypalForm = () => {
  const { copies } = useSelector((state) => state.whitelabelConfig);
  const { t } = useTranslation('payment');
  const { HIDE_PAYPAL_DEBIT_LOGOS } = useWhitelabelFeatures();

  const isV6Enabled = isPayPalV6Enabled();

  /**
   * Get appropriate instruction labels based on v6 status
   * @returns {Array<string>} Array of instruction labels
   */
  const getInstructionLabels = () => {
    if (isV6Enabled) {
      return [
        t('paypal_v6_instructions.first'),
        t('paypal_v6_instructions.second'),
        t('paypal_v6_instructions.third'),
        t('paypal_v6_instructions.fourth'),
      ];
    }
    return [
      t('paypal_instructions.first'),
      t('paypal_instructions.second'),
      t('paypal_instructions.third', { provider: copies.provider.name }),
      t('paypal_instructions.fourth'),
    ];
  };

  /**
   * Get title based on v6 status
   * @returns {string} Title string
   */
  const getTitle = () => {
    if (isV6Enabled) {
      return t('label.pay_with_paypal_v6');
    }
    return t('label.pay_with_payPal');
  };

  const labels = getInstructionLabels();

  return (
    <div className="checkout-section">
      <Spacing vertical>
        <div className="checkout-paypal-logos">
          <PaymentImage type="paypal-logo" />
          <Spacing isResponsive>
            <PaymentImage type="credit-options" />
            {!HIDE_PAYPAL_DEBIT_LOGOS && <PaymentImage type="debit-options" />}
          </Spacing>
        </div>
        <CheckList title={getTitle()} labels={labels} />
      </Spacing>
    </div>
  );
};

export default PaypalForm;
